import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  AvatarProps,
  Box,
  BoxProps,
  ButtonProps,
  Card,
  CardContent,
  CardProps,
  Container,
  Divider,
  Grid,
  IconProps,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import DropzoneS3Uploader from 'react-dropzone-s3-uploader';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import { useParams } from 'react-router-dom';
import { TP_COLORS } from '../../../constants';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import { useAppSelector } from '../../../hooks';
import {
  getCoursesByUserAsAdmin,
  getCourseStudentsState,
} from '../../../store/V2/CourseStudentsSlice';
import { getSubjects } from '../../../store/V2/SubjectSlice';
import { TUpdateUser, updateUser } from '../../../store/UserSlice/UserApi';
import {
  checkUserPermission,
  getFormatedDurationByMinutes,
  scrubFileName,
} from '../../../helper';
import { getUserByIdAsAdmin, getUsersState } from '../../../store/UserSlice';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import AdminBasicDetails from './BasicDetails';
import AdminNotificationSettings from './NotificationSettings';
import AdminQualificationsExperience from './QualificationsExperience';
import AdminSubjectCurriculum from './SubjectCurriculum';
import Subscriptions from '../Profile/Subscriptions';
import Plans from './Plans';
import TutorRates from './TutorRates';
import { USER_PERMISSION_INDIVIDUALS } from '../../../store/V2/RolesSlice';
import ClassStatement from './ClassStatement';
import {
  adminGetMeetings,
  getScheduleState,
  resetAdminMeetingsData,
  TAdminGetMeetings,
} from '../../../store/V2/ScheduleSlice';
import { getTenantsState } from '../../../store/V2/TenantsSlice';
import { getAuthState } from '../../../store/AuthSlice';

export const DashedDivider: React.FC = () => (
  <Divider flexItem sx={{ borderStyle: 'dashed', margin: '24px 0px' }} />
);

export const titleStackProps: StackProps = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: '24px',
};

export const primaryButtonProps: ButtonProps = {
  disableElevation: true,
  variant: 'contained',
  color: 'secondary',
  sx: {
    color: '#F8FAFC',
    backgroundColor: TP_COLORS.gray.darken,
    fontWeight: '600',
    padding: '6px 10px',
  },
};

const profileCardStyles: CardProps['sx'] = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: '621px',
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const profileBoxStyles: CardProps['sx'] = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  height: '128px',
  background:
    'linear-gradient(107.83deg, #003768 7.88%, #006C9C 41.91%, #39BFB9 82.16%)',
  borderRadius: '20px 20px 0px 0px',
};

const avatarStyles: AvatarProps['sx'] = {
  width: '128px',
  height: '128px',
  fontSize: '70px',
  border: '3px solid white',
};

const avatarBoxStyles: BoxProps['sx'] = {
  position: 'relative',
  bottom: '-37px',
};

const profileIconStyles: IconProps['sx'] = {
  position: 'absolute',
  width: '39px',
  height: '39px',
  color: '#F8FAFC',
};

const nameTypoProps: TypographyProps = {
  variant: 'h5',
  fontSize: '24px',
  fontWeight: '700',
  textAlign: 'center',
  marginTop: '57px',
  color: TP_COLORS.gray.darken,
};

const emailTypoProps: TypographyProps = {
  variant: 'subtitle2',
  textAlign: 'center',
  color: '#647488',
  margin: 0,
};

const listItemProps: ListItemButtonProps['sx'] = {
  height: '63px',
  borderRadius: '10px',
  '&.Mui-selected': {
    backgroundColor: '#F1F5F9',
  },
  '&:hover': {
    borderRadius: '10px',
  },
};

const renderTabCardProps: CardProps['sx'] = {
  sx: {
    minHeight: '600px',
    padding: '24px',
    borderRadius: '20px',
    boxShadow: '0px 14px 40px 0px #6E9DB22E',
  },
};

const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const dropzoneStyles = {
  height: 'inherit',
  width: 'inherit',
  border: 'none',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  cursor: 'pointer',
};

const notDropzoneProps = [
  'onFinish',
  's3Url',
  'filename',
  'host',
  'upload',
  'isImage',
  'notDropzoneProps',
  'onDrop',
];

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'link',
      title: 'Users',
      link: `${process.env.PUBLIC_URL}/portal/users`,
    },
    {
      type: 'text',
      title: 'Profile',
      highlight: true,
    },
  ];

  return options;
};

const AdminViewUser: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const urlParams = useParams();
  const userId = urlParams.id;

  // Store
  const authState = useAppSelector(getAuthState);
  const scheduleState = useAppSelector(getScheduleState);
  const usersState = useAppSelector(getUsersState);
  const tenantState = useAppSelector(getTenantsState);
  const courseStudentsState = useAppSelector(getCourseStudentsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { user: userData, status } = usersState;
  const { activeCourses, completedCourses } = courseStudentsState.subscriptions;

  // State
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);

  const { adminMeetingsData } = scheduleState;

  const breadcrumbOptions = constructBreadcrumbOptions();

  let userAvatar = '';
  let userAvatarFirstLetter = '';
  let totalUpcomingClassesCount = 0;

  if (userData) {
    if (userData.avatar) {
      userAvatar = userData.avatar;
    }

    if (userData.firstname) {
      userAvatarFirstLetter = userData.firstname.charAt(0);
    }
  }

  if (adminMeetingsData) {
    totalUpcomingClassesCount = adminMeetingsData.totalDocs || 0;
  }

  useEffect(() => {
    dispatch(getSubjects());

    if (userId) {
      dispatch(getUserByIdAsAdmin(userId));
      dispatch(
        getCoursesByUserAsAdmin({
          userId,
          deviceTimezone,
        })
      );
    }

    return () => {
      dispatch(resetAdminMeetingsData());
    };
  }, []);

  useEffect(() => {
    if (userData && !userLoaded) {
      const _studentPermission = checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          userTypes: [USER_PERMISSION_INDIVIDUALS.STUDENT],
        },
      });

      const _tutorPermission = checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          userTypes: [USER_PERMISSION_INDIVIDUALS.TUTOR],
        },
      });

      const apiData: TAdminGetMeetings = {
        startDate: moment().toISOString(),
        countOnly: true,
      };

      if (_studentPermission) {
        apiData.studentId = userData._id;
      } else if (_tutorPermission) {
        apiData.tutorId = userData._id;
      }

      if (apiData.studentId || apiData.tutorId) {
        dispatch(adminGetMeetings({ ...apiData }));
      }

      setUserLoaded(true);
    }
  }, [userData]);

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => {
    setSelectedIndex(index);
  };

  const handleFinishedUpload = async (file) => {
    const apiData: TUpdateUser = {
      avatar: `${process.env.REACT_APP_TUTOPIYA_S3_AVATAR_BUCKET_URL}/${file.objectName}`,
    };
    await dispatch(updateUser(apiData));

    setLoading(false);
  };

  const profileTabs = [
    { title: 'Basic Details', icon: 'basic-details', isEnabled: true },
    {
      title: 'Notification Settings',
      icon: 'notification-settings',
      isEnabled: Boolean(userData?.role.notifications),
    },
    {
      title: 'Qualifications & Experience',
      icon: 'qualifications-experience',
      isEnabled: checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_QUALIFICATIONS],
        },
      }),
    },
    {
      title: 'Subject & Curriculum',
      icon: 'subject-curriculum',
      isEnabled: checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_SUBJECTS],
        },
      }),
    },
    {
      title: 'Subscriptions',
      icon: 'subscription',
      isEnabled: activeCourses.length || completedCourses.length,
    },
    {
      title: 'Plans',
      icon: 'subscription',
      isEnabled: checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_PLANS],
        },
      }),
    },
    {
      title: 'Class Rates',
      icon: 'subscription',
      isEnabled: checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_TUTOR_RATES],
        },
      }),
    },
    {
      title: 'Class Statement',
      icon: 'paper',
      isEnabled: checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_CLASS_STATEMENT],
        },
      }),
    },
  ];

  const RENDER_TAB = () => {
    switch (selectedIndex) {
      case 0:
        return <AdminBasicDetails />;
      case 1:
        return <AdminNotificationSettings />;
      case 2:
        return <AdminQualificationsExperience />;
      case 3:
        return <AdminSubjectCurriculum />;
      case 4:
        return <Subscriptions />;
      case 5:
        return <Plans />;
      case 6:
        return <TutorRates />;
      case 7:
        return <ClassStatement />;
      default:
        return null;
    }
  };

  return (
    <>
      <Container maxWidth="lg">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor="#647488"
            showDropDown={isXsScreen}
          />
        </div>
        <Stack alignItems="center">
          <Grid container columnGap={3} rowGap={2} mb="15px" maxWidth="1060px">
            <Grid item xs={12} sm={4}>
              <div style={{ position: 'sticky', top: 'calc(64px + 15px)' }}>
                <Card sx={profileCardStyles}>
                  <CardContent
                    sx={{
                      padding: '10px',
                    }}
                  >
                    <Box sx={profileBoxStyles}>
                      {/* <MenuButton /> */}
                      <Box sx={avatarBoxStyles}>
                        <DropzoneS3Uploader
                          s3Url={
                            process.env.REACT_APP_TUTOPIYA_S3_AVATAR_BUCKET_URL
                          }
                          maxSize={1024 * 1024 * 100}
                          multiple={false}
                          onFinish={handleFinishedUpload}
                          onProgress={() => setLoading(true)}
                          onMouseOver={() => setIsHover(true)}
                          onMouseLeave={() => setIsHover(false)}
                          accept="image/*"
                          style={dropzoneStyles}
                          notDropzoneProps={notDropzoneProps}
                          upload={{
                            server: `${process.env.REACT_APP_TUTOPIYA_BASE_URL_V2}/user/avatar`,
                            signingUrlQueryParams: {},
                            scrubFilename: scrubFileName,
                          }}
                        >
                          <>
                            {userAvatar ? (
                              <Avatar
                                src={userAvatar}
                                sx={{
                                  ...avatarStyles,
                                  filter: `brightness(${isHover ? 0.5 : 1})`,
                                }}
                              />
                            ) : (
                              <Avatar
                                sx={{
                                  ...avatarStyles,
                                  filter: `brightness(${isHover ? 0.5 : 1})`,
                                }}
                              >
                                {userAvatarFirstLetter}
                              </Avatar>
                            )}
                            {isLoading && (
                              <BasicLoader
                                containerProps={{ position: 'absolute' }}
                              />
                            )}

                            {isHover && (
                              <PhotoCameraIcon sx={profileIconStyles} />
                            )}
                          </>
                        </DropzoneS3Uploader>
                      </Box>
                    </Box>
                    <Typography {...nameTypoProps}>
                      {userData?.firstname} {userData?.lastname}
                    </Typography>
                    <Typography {...emailTypoProps}>
                      {userData?.email}
                    </Typography>
                    {userData ? (
                      <>
                        {'remainingTime' in userData ? (
                          <Typography {...emailTypoProps}>
                            Credits:&nbsp;
                            {getFormatedDurationByMinutes({
                              minutes: userData.remainingTime || 0,
                            })}
                          </Typography>
                        ) : null}
                        <Typography {...emailTypoProps}>
                          Upcoming classes count:&nbsp;
                          {totalUpcomingClassesCount}
                        </Typography>
                      </>
                    ) : null}

                    <List sx={{ mt: '22px' }}>
                      {profileTabs.map((element, index) => {
                        if (element.isEnabled) {
                          return (
                            <ListItemButton
                              key={index}
                              selected={selectedIndex === index}
                              onClick={(event) =>
                                handleListItemClick(event, index)
                              }
                              sx={listItemProps}
                            >
                              <ListItemIcon>
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/profile/icon-${element.icon}.png`}
                                  alt="qualification-icon"
                                />
                              </ListItemIcon>

                              <ListItemText primary={element.title} />
                            </ListItemButton>
                          );
                        }
                        return null;
                      })}
                    </List>
                  </CardContent>
                </Card>
              </div>
            </Grid>

            <Grid item xs={12} sm>
              <Card {...renderTabCardProps}>
                {!userData && status === 'loading' ? (
                  <BasicLoader
                    containerProps={{
                      minHeight: '300px',
                      alignItems: 'center',
                    }}
                  />
                ) : (
                  RENDER_TAB()
                )}
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </>
  );
};

export default AdminViewUser;
